import type { PayoutRequest, PayoutView } from '@/features/onboarding/types';
import { api, ApiError } from '@/lib/api';

export const PET_SPECIES = ['DOG', 'CAT', 'BIRD', 'RABBIT', 'FISH', 'OTHER'] as const;
export type PetSpecies = (typeof PET_SPECIES)[number];

export type VendorProfile = {
  business_name: string | null;
  owner_name: string | null;
  description: string | null;
  email_for_business: string | null;
  website_url: string | null;
  gst_number: string | null;
  business_registration_number: string | null;
  business_phone: string | null;
  /** OTP login phone of the owning account (read-only). */
  account_phone: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  is_24x7: boolean;
  services_species: PetSpecies[];
  /** DEPRECATED and inert — nothing on the platform reads it. See `service_area`. */
  delivery_radius_km: number;
  /** The radius that actually decides who can find this vendor, per module. */
  service_area: ServiceArea | null;
  booking_min_notice_hours: number;
  booking_horizon_days: number;
  /** Food/medicine delivery window in minutes (client change, 9 Oct 2026); null → platform default. */
  delivery_eta_min_minutes: number | null;
  delivery_eta_max_minutes: number | null;
  reschedule_min_notice_hours: number;
  module_types: string[];
  kyc_status: string;
  submitted_at: string | null;
  /**
   * Where the vendor operates from. Editable here since the profile endpoint
   * took over from the onboarding address step, which locks on submission.
   */
  address: string | null;
  landmark: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  /**
   * The map pin. Discovery matches on this, not on the printed address, so an
   * address changed without it leaves the vendor findable from where they used
   * to be. Null for vendors onboarded before coordinates were collected.
   */
  latitude: number | null;
  longitude: number | null;
  /**
   * Set on the PATCH response when the address just changed but no new pin came
   * with it. Not part of the stored profile — it describes the write that just
   * happened, so read it from the mutation result, not from a later GET.
   */
  location_needs_review?: boolean;
  logo_url: string | null;
  cover_url: string | null;
  /**
   * The photo of the PERSON, for walker and trainer modules — not the logo,
   * which pictures a business. Null for every other module, and for a walker who
   * onboarded before it was asked for.
   */
  profile_photo: { id: string; file_path: string } | null;
};

export type UpdateVendorProfileInput = {
  business_phone?: string | null;
  instagram_url?: string | null;
  facebook_url?: string | null;
  services_species?: PetSpecies[];
  delivery_radius_km?: number;
  booking_min_notice_hours?: number;
  booking_horizon_days?: number;
  delivery_eta_min_minutes?: number | null;
  delivery_eta_max_minutes?: number | null;
  reschedule_min_notice_hours?: number;
  is_24x7?: boolean;
  address?: string;
  landmark?: string | null;
  city?: string;
  state?: string;
  pincode?: string;
  /**
   * Send both or neither — the server rejects a lone coordinate, because half a
   * pin would move the vendor to the equator.
   *
   * OMIT them to leave the pin untouched. Do not helpfully resend the values you
   * read back: the server distinguishes "moved and re-pinned" from "moved but did
   * not re-pin" by whether these arrive, and resending the old ones tells it the
   * pin was reviewed when it was not.
   */
  latitude?: number;
  longitude?: number;
};

export async function fetch_vendor_profile(): Promise<VendorProfile> {
  const res = await api.get<VendorProfile>('/vendor/profile');
  return res.data;
}

export async function update_vendor_profile(
  input: UpdateVendorProfileInput,
): Promise<VendorProfile> {
  const res = await api.patch<VendorProfile>('/vendor/profile', input);
  return res.data;
}

/**
 * The vendor's three single-slot pictures. `photo` is the walker/trainer face;
 * the endpoint has always existed but nothing here called it, so a walker could
 * set their face during onboarding and never change it again.
 */
export type VendorImageKind = 'logo' | 'cover' | 'photo';

export async function upload_vendor_image(
  kind: VendorImageKind,
  file: File,
): Promise<VendorProfile> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<VendorProfile>(`/vendor/profile/${kind}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export type ServiceAreaModule = {
  module_type: string;
  /** Null when never declared — `effective_radius_km` then shows the platform default. */
  radius_km: number | null;
  effective_radius_km: number;
  is_declared: boolean;
  /** False for AT-SALON modules where the radius is recorded but not used to filter. */
  filters_discovery: boolean;
  options: number[];
};

export type ServiceArea = {
  modules: ServiceAreaModule[];
  default_radius_km: number;
};

/** Set one module's radius. Allowed values differ per module — see `options`. */
export async function set_service_area(input: {
  module_type: string;
  radius_km: number;
}): Promise<ServiceAreaModule> {
  const res = await api.put<ServiceAreaModule>('/vendor/profile/service-area', input);
  return res.data;
}

/**
 * The masked payout account, post-onboarding.
 *
 * 404 means this profile has no payout details at all — only possible for vendors who onboarded
 * before the step became mandatory. Mapped to `null` here so the card can offer "set these up"
 * rather than rendering an error state at someone whose account is simply older than the rule.
 */
export async function fetch_payout(): Promise<PayoutView | null> {
  try {
    const res = await api.get<PayoutView>('/vendor/profile/payout');
    return res.data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * Add payout details for the first time.
 *
 * Create-only, and the counterpart to the 404 above: `replace_payout` refuses when there is
 * nothing to replace, and the onboarding step is behind a draft an approved vendor no longer has.
 * Without this the card could only say "contact support", which meant an admin typing someone
 * else's bank details on their behalf.
 */
export async function create_payout(input: PayoutRequest): Promise<PayoutView> {
  const res = await api.post<PayoutView>('/vendor/profile/payout', input);
  return res.data;
}

/** Replace the whole account. Allowed once every 30 days; 409 PAYOUT_EDIT_TOO_SOON otherwise. */
export async function replace_payout(input: PayoutRequest): Promise<PayoutView> {
  const res = await api.put<PayoutView>('/vendor/profile/payout', input);
  return res.data;
}
